import { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AsYouType, isSupportedCountry } from "libphonenumber-js";
import { boxModalStyle } from "../../BookingStyle";

const DEFAULT_PHONE_COUNTRY = "PE";
const DEFAULT_PHONE_DIAL_CODE = "+51";

const formatPhoneByCountry = (phone, countryCode) => {
  const formatterCountry = isSupportedCountry(countryCode)
    ? countryCode
    : DEFAULT_PHONE_COUNTRY;

  return new AsYouType(formatterCountry).input(phone);
};

const getCountryFlag = (countryCode) => {
  if (!/^[A-Z]{2}$/.test(countryCode)) return "";

  return countryCode
    .split("")
    .map((letter) => String.fromCodePoint(letter.charCodeAt(0) + 127397))
    .join("");
};

const OtherPersonModal = ({
  open,
  onClose,
  otherPersonName,
  otherPersonPhone,
  onNameChange,
  onPhoneChange,
  onAccept,
  fieldErrors = {},
}) => {
  const [phone, setPhone] = useState("");
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY);
  const [phoneCountries, setPhoneCountries] = useState([]);
  const [isCountrySelectOpen, setIsCountrySelectOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");

  useEffect(() => {
    fetch("/assets/countries.json")
      .then((response) => response.json())
      .then((countries) => {
        if (Array.isArray(countries)) {
          setPhoneCountries(countries);
        }
      })
      .catch(() => {
        setPhoneCountries([]);
      });
  }, []);

  useEffect(() => {
    const trimmedPhone = otherPersonPhone.trim();
    const matchedCountry = phoneCountries
      .filter((country) => trimmedPhone.startsWith(country.dial_code))
      .sort((first, second) => second.dial_code.length - first.dial_code.length)[0];
    const nextCountry = matchedCountry?.code ?? DEFAULT_PHONE_COUNTRY;
    const nationalPhone = matchedCountry
      ? trimmedPhone.slice(matchedCountry.dial_code.length).trim()
      : trimmedPhone;
    const rawPhone = nationalPhone.replace(/[^\d]/g, "");

    setPhoneCountry(nextCountry);
    setPhone(formatPhoneByCountry(rawPhone, nextCountry));
  }, [otherPersonPhone, phoneCountries]);

  const selectedPhoneCountry = useMemo(
    () => phoneCountries.find((country) => country.code === phoneCountry),
    [phoneCountries, phoneCountry],
  );

  const filteredPhoneCountries = useMemo(() => {
    const searchValue = countrySearch.trim().toLowerCase();

    if (!searchValue) return phoneCountries;

    return phoneCountries.filter(
      (country) =>
        country.name.toLowerCase().includes(searchValue) ||
        country.dial_code.includes(searchValue),
    );
  }, [countrySearch, phoneCountries]);

  const closeCountrySelect = () => {
    setIsCountrySelectOpen(false);
    setCountrySearch("");
  };

  const emitPhoneChange = (nextPhone, nextCountryCode) => {
    const country =
      phoneCountries.find((item) => item.code === nextCountryCode) ??
      selectedPhoneCountry;
    const phoneDialCode = country?.dial_code ?? DEFAULT_PHONE_DIAL_CODE;
    const nextValue = nextPhone.trim() ? `${phoneDialCode} ${nextPhone.trim()}` : "";

    onPhoneChange({
      target: {
        name: "otherPersonPhone",
        value: nextValue,
      },
    });
  };

  const handlePhoneChange = (event) => {
    const rawValue = event.target.value.replace(/[^\d]/g, "");
    const formattedPhone = formatPhoneByCountry(rawValue, phoneCountry);

    setPhone(formattedPhone);
    emitPhoneChange(formattedPhone, phoneCountry);
  };

  const handlePhoneCountryChange = (event) => {
    const countryCode = event.target.value;
    const rawValue = phone.replace(/[^\d]/g, "");
    const formattedPhone = formatPhoneByCountry(rawValue, countryCode);

    setPhoneCountry(countryCode);
    setPhone(formattedPhone);
    emitPhoneChange(formattedPhone, countryCode);
  };

  const renderSelectedDialCode = (value) => {
    const country = phoneCountries.find((item) => item.code === value);

    if (!country) return DEFAULT_PHONE_DIAL_CODE;

    return `${getCountryFlag(country.code)} ${country.dial_code}`;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="other-person-modal-title"
    >
      <Box
        sx={{
          ...boxModalStyle,
          width: {
            xs: "90%",
            sm: "420px",
          },
        }}
      >
        <Typography
          id="other-person-modal-title"
          variant="h6"
          component="h2"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          Datos de otra persona
        </Typography>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Nombre y Apellido"
            name="otherPersonName"
            value={otherPersonName}
            onChange={onNameChange}
            error={!!fieldErrors.otherPersonName}
            helperText={
              fieldErrors.otherPersonName ? "Ingrese nombre y apellido" : ""
            }
          />
          <TextField
            fullWidth
            label="Telefono"
            name="otherPersonPhone"
            type="tel"
            value={phone}
            onChange={handlePhoneChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Select
                    variant="standard"
                    value={phoneCountry}
                    open={isCountrySelectOpen}
                    onOpen={() => setIsCountrySelectOpen(true)}
                    onClose={closeCountrySelect}
                    onChange={(event) => {
                      handlePhoneCountryChange(event);
                      closeCountrySelect();
                    }}
                    disableUnderline
                    renderValue={renderSelectedDialCode}
                    MenuProps={{
                      autoFocus: false,
                      PaperProps: {
                        sx: {
                          maxHeight: 320,
                          width: 280,
                        },
                      },
                    }}
                    sx={{ minWidth: 78, fontSize: 14 }}
                  >
                    <ListSubheader
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 1,
                      }}
                    >
                      <TextField
                        autoFocus
                        size="small"
                        placeholder="Buscar"
                        value={countrySearch}
                        onChange={(event) => setCountrySearch(event.target.value)}
                        onKeyDown={(event) => event.stopPropagation()}
                        fullWidth
                      />
                      <IconButton
                        size="small"
                        onClick={closeCountrySelect}
                        aria-label="Cerrar"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </ListSubheader>

                    {phoneCountries.length === 0 && (
                      <MenuItem value={phoneCountry}>
                        {getCountryFlag(phoneCountry)} {DEFAULT_PHONE_DIAL_CODE}
                      </MenuItem>
                    )}

                    {filteredPhoneCountries.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {getCountryFlag(country.code)} {country.dial_code}{" "}
                        {country.name}
                      </MenuItem>
                    ))}

                    {phoneCountries.length > 0 &&
                      filteredPhoneCountries.length === 0 && (
                        <MenuItem disabled>No hay resultados</MenuItem>
                      )}
                  </Select>
                </InputAdornment>
              ),
            }}
            inputProps={{ maxLength: 25 }}
            error={!!fieldErrors.otherPersonPhone}
            helperText={fieldErrors.otherPersonPhone ? "Ingrese el telefono" : ""}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              pt: 1,
            }}
          >
            
            <Button variant="contained" color="error" onClick={onClose}>
              Cancelar
            </Button>
            <Button variant="contained" color="primary" onClick={onAccept}>
              Aceptar
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};

export default OtherPersonModal;
